// audio-context.tsx
import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
} from "react";

type AudioContextType = {
  play: () => Promise<void>;
  pause: () => void;
  audioRef: React.RefObject<HTMLAudioElement>;
  isReady: boolean;
};

const AudioContext = createContext<AudioContextType | null>(null);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const audio = new Audio();
    // Explicitly set src to ensure it's properly set
    const audioPath = "/audio/Joakim-Karud-Dreams.mp3";
    audio.src = audioPath;
    audio.loop = true;
    audio.volume = 0.5;
    audio.preload = "auto";
    audio.crossOrigin = "anonymous"; // Help with CORS if needed

    // Handle successful load
    const handleCanPlay = () => {
      setIsReady(true);
    };

    // Handle load errors
    const handleError = (e: Event) => {
      const audioEl = e.target as HTMLAudioElement;
      const error = audioEl.error;
      if (error) {
        console.error("Audio failed to load:", {
          code: error.code,
          message: error.message,
          src: audioEl.src,
          networkState: audioEl.networkState,
          readyState: audioEl.readyState,
        });
      }
      setIsReady(false);
    };

    // Handle when audio can start playing
    const handleCanPlayThrough = () => {
      setIsReady(true);
    };

    // Handle when source is loaded
    const handleLoadedData = () => {
      setIsReady(true);
    };

    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);
    audio.addEventListener("loadeddata", handleLoadedData);
    audio.addEventListener("error", handleError);

    // Explicitly load the audio
    audio.load();

    audioRef.current = audio;

    return () => {
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
      audio.removeEventListener("loadeddata", handleLoadedData);
      audio.removeEventListener("error", handleError);
      audio.pause();
      audio.currentTime = 0;
      audio.src = ""; // Clear src to stop loading
    };
  }, []);

  const play = async () => {
    if (!audioRef.current) {
      console.warn("Audio ref not available");
      return;
    }

    // Check for errors first
    if (audioRef.current.error) {
      console.error("Audio has error:", {
        code: audioRef.current.error.code,
        message: audioRef.current.error.message,
        src: audioRef.current.src,
        networkState: audioRef.current.networkState,
      });
      // Try to reload if there's an error
      try {
        audioRef.current.load();
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (err) {
        console.error("Failed to reload audio:", err);
        return;
      }
    }

    // Check if audio has a valid source
    if (
      !audioRef.current.src ||
      audioRef.current.src.endsWith(window.location.href)
    ) {
      console.warn("Audio source not set, setting source and loading");
      audioRef.current.src = "/audio/Joakim-Karud-Dreams.mp3";
      audioRef.current.load();
      // Wait for it to load
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    // Check if audio has loaded (readyState 2 = HAVE_CURRENT_DATA, 3 = HAVE_FUTURE_DATA, 4 = HAVE_ENOUGH_DATA)
    if (audioRef.current.readyState < 2) {
      console.warn(
        "Audio not ready, readyState:",
        audioRef.current.readyState,
        "waiting..."
      );
      // Wait for it to load with a promise that resolves on canplay
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("Audio load timeout"));
        }, 3000);

        const handleCanPlay = () => {
          clearTimeout(timeout);
          audioRef.current?.removeEventListener("canplay", handleCanPlay);
          audioRef.current?.removeEventListener("error", handleError);
          resolve();
        };

        const handleError = () => {
          clearTimeout(timeout);
          audioRef.current?.removeEventListener("canplay", handleCanPlay);
          audioRef.current?.removeEventListener("error", handleError);
          reject(new Error("Audio failed to load"));
        };

        if (audioRef.current) {
          audioRef.current.addEventListener("canplay", handleCanPlay, {
            once: true,
          });
          audioRef.current.addEventListener("error", handleError, {
            once: true,
          });
          // Trigger load if not already loading
          if (audioRef.current.networkState === 0) {
            audioRef.current.load();
          }
        } else {
          clearTimeout(timeout);
          reject(new Error("Audio ref lost"));
        }
      }).catch((err) => {
        console.error("Audio load failed:", err);
        return;
      });
    }

    // Final check for errors before playing
    if (audioRef.current.error) {
      console.error(
        "Audio still has error after load attempt:",
        audioRef.current.error
      );
      return;
    }

    try {
      audioRef.current.muted = false;
      await audioRef.current.play();
    } catch (err) {
      console.warn("Audio play blocked", err);
    }
  };

  const pause = () => {
    audioRef.current?.pause();
  };

  return (
    <AudioContext.Provider value={{ play, pause, audioRef, isReady }}>
      {children}
    </AudioContext.Provider>
  );
};

export const useAudio = () => {
  const ctx = useContext(AudioContext);
  if (!ctx) throw new Error("useAudio must be used inside AudioProvider");
  return ctx;
};
